import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, Check, Trash2, Mail, MessageCircle, Loader2, FileSpreadsheet, Star, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const AdminTools = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [reviews, setReviews] = useState([]);
  const [newsletterSubs, setNewsletterSubs] = useState({ total: 0, subscribers: [] });
  const [wholesale, setWholesale] = useState([]);
  const [reviewStatus, setReviewStatus] = useState('pending');

  // CSV upload state
  const [csvFile, setCsvFile] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user || !user.is_admin) {
      navigate('/');
      return;
    }
    fetchAll();
  }, [user, loading]);

  const fetchAll = () => {
    fetchReviews(reviewStatus);
    fetchNewsletter();
    fetchWholesale();
  };

  const fetchReviews = async (status) => {
    try {
      const r = await api.get(`/admin/reviews?status=${status}`);
      setReviews(r.data);
    } catch (_) { /* soft */ }
  };

  const fetchNewsletter = async () => {
    try {
      const r = await api.get('/admin/newsletter-subscribers');
      setNewsletterSubs(r.data);
    } catch (_) { /* soft */ }
  };

  const fetchWholesale = async () => {
    try {
      const r = await api.get('/admin/wholesale-inquiries');
      setWholesale(r.data);
    } catch (_) { /* soft */ }
  };

  const handleReviewStatusChange = (status) => {
    setReviewStatus(status);
    fetchReviews(status);
  };

  const approveReview = async (id) => {
    try {
      await api.put(`/admin/reviews/${id}/approve`);
      toast.success('Review approved');
      fetchReviews(reviewStatus);
    } catch (_) {
      toast.error('Approval failed');
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm('Delete this review?')) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success('Review deleted');
      fetchReviews(reviewStatus);
    } catch (_) {
      toast.error('Delete failed');
    }
  };

  const updateInquiryStatus = async (id, status) => {
    try {
      await api.put(`/admin/wholesale-inquiries/${id}/status?status=${status}`);
      toast.success('Status updated');
      fetchWholesale();
    } catch (_) {
      toast.error('Update failed');
    }
  };

  const handleCsvUpload = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }
    if (imageFiles.length === 0) {
      toast.error('Please upload at least one image');
      return;
    }
    const fd = new FormData();
    fd.append('csv_file', csvFile);
    imageFiles.forEach((f) => fd.append('images', f));

    try {
      setUploading(true);
      setUploadResult(null);
      const resp = await api.post('/admin/products/bulk-csv', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadResult(resp.data);
      if (resp.data.error_count === 0) {
        toast.success(`Created ${resp.data.created_count} products successfully!`);
      } else {
        toast.warning(`Created ${resp.data.created_count}, ${resp.data.error_count} errors`);
      }
      setCsvFile(null);
      setImageFiles([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setUploading(false);
    }
  };

  const downloadSampleCsv = () => {
    const sample = `name,description,price,image_filename,category,fabric,colour,stock,tags
Red Silk Bandhani Saree,Handcrafted red silk saree with gold border,12500,saree_red.jpg,Sarees,Silk,Red,3,best_seller
Blue Cotton Dupatta,Light indigo dupatta perfect for daily wear,3500,dupatta_blue.jpg,Dupattas,Cotton,Blue,10,
`;
    const blob = new Blob([sample], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bandhani_products_sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportEmails = () => {
    const csv = 'email,subscribed_at\n' + newsletterSubs.subscribers.map(s => `${s.email},${s.subscribed_at}`).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'newsletter_subscribers.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="py-24 md:py-32 max-w-[1400px] mx-auto px-4 md:px-8" data-testid="admin-tools-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <Button variant="ghost" onClick={() => navigate('/admin')} className="mb-3 rounded-full" data-testid="admin-tools-back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
          </Button>
          <h1 className="text-4xl md:text-5xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Store Tools
          </h1>
        </div>
      </div>

      <Tabs defaultValue="reviews" className="w-full">
        <TabsList className="mb-8 grid grid-cols-2 md:grid-cols-4 w-full max-w-3xl">
          <TabsTrigger value="reviews" data-testid="tools-reviews-tab">
            <Star className="h-3.5 w-3.5 mr-1.5" />
            Reviews
          </TabsTrigger>
          <TabsTrigger value="bulk" data-testid="tools-bulk-tab">
            <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />
            Bulk Upload
          </TabsTrigger>
          <TabsTrigger value="newsletter" data-testid="tools-newsletter-tab">
            <Mail className="h-3.5 w-3.5 mr-1.5" />
            Newsletter
          </TabsTrigger>
          <TabsTrigger value="wholesale" data-testid="tools-wholesale-tab">
            <MessageCircle className="h-3.5 w-3.5 mr-1.5" />
            Wholesale
          </TabsTrigger>
        </TabsList>

        {/* REVIEWS */}
        <TabsContent value="reviews">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-medium" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Review Moderation</h2>
            <div className="flex gap-2">
              {['pending', 'approved', 'all'].map((s) => (
                <button
                  key={s}
                  onClick={() => handleReviewStatusChange(s)}
                  className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${
                    reviewStatus === s ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:border-primary/40'
                  }`}
                  data-testid={`review-filter-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          {reviews.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No {reviewStatus === 'all' ? '' : reviewStatus} reviews.</p>
          ) : (
            <div className="space-y-4">
              {reviews.map((r) => (
                <div key={r.id} className="border border-border rounded-xl p-5" data-testid={`admin-review-${r.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="font-medium">{r.user_name} <span className="text-xs text-muted-foreground">on</span> {r.product_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleString('en-IN')}
                      </p>
                    </div>
                    <span className={`text-xs uppercase tracking-widest px-3 py-1 rounded-full ${
                      r.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>{r.status}</span>
                  </div>
                  <p className="text-sm mb-4">{r.text}</p>
                  <div className="flex gap-2">
                    {r.status !== 'approved' && (
                      <Button size="sm" onClick={() => approveReview(r.id)} className="rounded-full" data-testid={`review-approve-${r.id}`}>
                        <Check className="h-3.5 w-3.5 mr-1" /> Approve
                      </Button>
                    )}
                    <Button size="sm" variant="destructive" onClick={() => deleteReview(r.id)} className="rounded-full" data-testid={`review-delete-${r.id}`}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* BULK CSV */}
        <TabsContent value="bulk">
          <div className="border border-border rounded-xl p-6 md:p-8">
            <h2 className="text-2xl font-medium mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Bulk Product Upload</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Upload a CSV file plus your product images. Each CSV row must reference an image filename that exists in the uploaded images.
            </p>

            <Button onClick={downloadSampleCsv} variant="outline" className="rounded-full mb-6" data-testid="download-sample-csv-btn">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Download Sample CSV
            </Button>

            <form onSubmit={handleCsvUpload} className="space-y-5" data-testid="bulk-upload-form">
              <div>
                <Label className="text-xs uppercase tracking-widest">CSV File *</Label>
                <Input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setCsvFile(e.target.files[0])}
                  className="mt-2 rounded-full"
                  required
                  data-testid="csv-file-input"
                />
                {csvFile && <p className="text-xs text-muted-foreground mt-1">Selected: {csvFile.name}</p>}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Product Images *</Label>
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => setImageFiles(Array.from(e.target.files))}
                  className="mt-2 rounded-full"
                  required
                  data-testid="images-input"
                />
                {imageFiles.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">{imageFiles.length} image(s) selected</p>
                )}
              </div>
              <Button type="submit" disabled={uploading} className="rounded-full uppercase tracking-widest text-xs" data-testid="bulk-upload-submit-btn">
                {uploading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading…</> : <><Upload className="mr-2 h-4 w-4" /> Upload Products</>}
              </Button>
            </form>

            {uploadResult && (
              <div className="mt-6 border border-border rounded-xl p-5" data-testid="upload-result">
                <p className="font-medium mb-2">
                  Created: <span className="text-green-600">{uploadResult.created_count}</span> ·
                  Errors: <span className="text-red-600">{uploadResult.error_count}</span>
                </p>
                {uploadResult.created.length > 0 && (
                  <details className="mb-2">
                    <summary className="text-sm text-muted-foreground cursor-pointer">Show created ({uploadResult.created.length})</summary>
                    <ul className="text-xs mt-2 space-y-1">
                      {uploadResult.created.map((c) => (
                        <li key={c.id}>Row {c.row}: {c.name}</li>
                      ))}
                    </ul>
                  </details>
                )}
                {uploadResult.errors.length > 0 && (
                  <details open>
                    <summary className="text-sm text-red-600 cursor-pointer">Show errors ({uploadResult.errors.length})</summary>
                    <ul className="text-xs mt-2 space-y-1 text-red-700">
                      {uploadResult.errors.map((e, i) => (
                        <li key={i}>Row {e.row}: {e.error}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        {/* NEWSLETTER */}
        <TabsContent value="newsletter">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-medium" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Newsletter Subscribers</h2>
              <p className="text-sm text-muted-foreground">Total: {newsletterSubs.total}</p>
            </div>
            {newsletterSubs.total > 0 && (
              <Button variant="outline" onClick={exportEmails} className="rounded-full" data-testid="newsletter-export-btn">
                Export CSV
              </Button>
            )}
          </div>
          {newsletterSubs.total === 0 ? (
            <p className="text-sm text-muted-foreground italic">No subscribers yet.</p>
          ) : (
            <div className="border border-border rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-secondary">
                  <tr>
                    <th className="text-left p-3 text-xs uppercase tracking-widest">Email</th>
                    <th className="text-left p-3 text-xs uppercase tracking-widest">Subscribed</th>
                  </tr>
                </thead>
                <tbody>
                  {newsletterSubs.subscribers.map((s) => (
                    <tr key={s.id} className="border-t border-border" data-testid={`sub-${s.id}`}>
                      <td className="p-3 text-sm">{s.email}</td>
                      <td className="p-3 text-sm text-muted-foreground">{new Date(s.subscribed_at).toLocaleDateString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* WHOLESALE */}
        <TabsContent value="wholesale">
          <h2 className="text-2xl font-medium mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Wholesale Inquiries</h2>
          <p className="text-sm text-muted-foreground mb-6">Total: {wholesale.length}</p>
          {wholesale.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No inquiries yet.</p>
          ) : (
            <div className="space-y-4">
              {wholesale.map((w) => (
                <div key={w.id} className="border border-border rounded-xl p-5" data-testid={`wholesale-inquiry-${w.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="font-medium">{w.company_name} — {w.contact_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(w.created_at).toLocaleString('en-IN')}
                      </p>
                    </div>
                    <select
                      value={w.status}
                      onChange={(e) => updateInquiryStatus(w.id, e.target.value)}
                      className="text-xs uppercase tracking-widest px-3 py-1 rounded-full border border-border bg-background"
                      data-testid={`inquiry-status-${w.id}`}
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="converted">Converted</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mb-3">
                    <div><span className="text-muted-foreground">Phone: </span><a href={`tel:${w.phone}`} className="hover:text-primary">{w.phone}</a></div>
                    {w.email && <div><span className="text-muted-foreground">Email: </span><a href={`mailto:${w.email}`} className="hover:text-primary">{w.email}</a></div>}
                    <div className="md:col-span-2"><span className="text-muted-foreground">Price ranges: </span>{(w.price_ranges || []).join(', ') || 'Not specified'}</div>
                    {w.message && <div className="md:col-span-2"><span className="text-muted-foreground">Notes: </span>{w.message}</div>}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {w.business_card_url && (
                      <a
                        href={w.business_card_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs bg-secondary hover:bg-primary/10 px-3 py-1.5 rounded-full border border-border"
                      >
                        View Business Card
                      </a>
                    )}
                    <a
                      href={`https://wa.me/${(w.phone || '').replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs bg-green-100 text-green-800 hover:bg-green-200 px-3 py-1.5 rounded-full flex items-center gap-1"
                    >
                      <MessageCircle className="h-3 w-3" /> Reply on WhatsApp
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminTools;
